import { IBlogItem, IBlogItemOverview } from 'common/types/blog';
import { IAnyType, IServerProps } from 'common/types/common';
import { Article } from 'common/types/content/graphql/graphql';
import { getValidatedDynamicParam } from 'common/utils/dynamicRoute';
import {
  buildBreadcrumbListSchema,
  extractFaqSchemaFromBlogContent,
} from 'common/utils/structuredData';
import { buildAbsoluteUrl } from 'common/utils/url';
import { createApolloClient } from 'core/clientContent';
import { ENV_VARIABLES } from 'core/config';
import { QUERY_CONTENT_POSTS } from 'core/query/posts';
import { Layout } from 'layout';
import { ContentArticleContent } from 'modules/content/ContentArticleContent';
import { HeroContentArticle } from 'modules/content/HeroContentArticle';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const HeroBlog = dynamic(() => import('modules/blog/HeroBlog').then(mod => mod.HeroBlog));
const BlogContent = dynamic(() => import('modules/blog/BlogContent').then(mod => mod.BlogContent));
const CTA = dynamic(() => import('common/components/Cta').then(mod => mod.CTA));
const BlogItemsLatest = dynamic(() =>
  import('modules/blog/BlogItemsLatest').then(mod => mod.BlogItemsLatest),
);

const BlogSinglePage = (pageProps: IBlogSinglePageProps) => {
  const { i18n, t } = useTranslation([pageProps.data.detailsFileName, 'blogItemsOverview']);

  const router = useRouter();

  const blogItemsOverviewData = t('blogItemsOverview:blogItems', {
    returnObjects: true,
    defaultValue: [],
  }) as unknown;
  const blogItemsOverview = Array.isArray(blogItemsOverviewData)
    ? (blogItemsOverviewData as IBlogItemOverview[])
    : [];
  const latestBlogs = blogItemsOverview.filter(item => item.slug !== `/blog/${router.query.key}`);
  const blogItemOverview: IBlogItemOverview | undefined = blogItemsOverview.find(
    item => item.slug === `/blog/${router.query.key}`,
  );

  const article = pageProps.data.type === 'content' ? pageProps.data.article : null;
  let blog: IBlogItem | undefined;
  if (pageProps.data.type === 'blog' && pageProps.data.detailsFileName) {
    blog = i18n.getResourceBundle(i18n.language, pageProps.data.detailsFileName);
  }

  const author = article
    ? {
        name: article.author?.name || '',
        url: article.author?.linkedin || '',
      }
    : {
        name: blogItemOverview?.author.name || '',
        url: blogItemOverview?.author.url || '',
      };

  const articleAlternateLocalization =
    article?.localizations?.find(localization => localization?.locale !== article.locale) || null;

  const seo = article
    ? {
        alternate: articleAlternateLocalization?.slug
          ? {
              hrefLang: articleAlternateLocalization.locale || '',
              slug: `/blog/${articleAlternateLocalization.slug}`,
            }
          : undefined,
        title: article.seo?.title || article.title,
        description: article.seo?.description || article.description,
        image: article.seo?.image?.url || article.cover?.url || '',
        languageSwitch: {
          localizations:
            article.localizations?.map(localization => ({
              locale: localization?.locale,
              slug: localization?.slug,
            })) || [],
          routePrefix: '/blog',
        },
        slug: `/blog/${article.slug}`,
      }
    : {
        alternate: blogItemOverview?.seo.alternate || blog?.seo?.alternate,
        title: blogItemOverview?.seo.title,
        description: blogItemOverview?.seo.description,
        image: blogItemOverview?.seo.image,
        slug: blogItemOverview?.slug,
      };
  const base = t('common:company.url');
  const menuItems = t('common:menu.items', { returnObjects: true }) as Array<{
    link: string;
    title: string;
  }>;
  const homeLabel = menuItems.find(item => item.link === '/')?.title || 'Home';
  const blogLabel = menuItems.find(item => item.link === '/blog')?.title || 'Blog';
  const blogListUrl = buildAbsoluteUrl(base, '/blog');
  const pageUrl = seo.slug ? buildAbsoluteUrl(base, seo.slug) : blogListUrl;
  const publisher = {
    '@type': 'Organization',
    name: 'Nordit d.o.o.',
    url: base,
    logo: {
      '@type': 'ImageObject',
      url: 'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20white%2FNordit%20logo%20-%20primary%20white%402x.png?alt=media&token=d979ae78-414f-4fd1-bfb5-dc31ad8ce3cf',
    },
  };
  const breadcrumbSchema = buildBreadcrumbListSchema([
    { name: homeLabel, url: buildAbsoluteUrl(base, '/') },
    { name: blogLabel, url: blogListUrl },
    { name: seo.title || blog?.title || article?.title || 'Blog', url: pageUrl },
  ]);
  const articleTags = article?.tags?.map(tag => tag?.name).filter(Boolean) || [];
  const staticBlogTags = blogItemOverview?.tags?.map(tag => tag.label) || [];
  const faqSchema = blog ? extractFaqSchemaFromBlogContent(blog.content) : [];
  const blogPostingSchema =
    article || blogItemOverview
      ? {
          '@type': 'BlogPosting',
          mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
          headline: seo.title,
          image: seo.image,
          description: seo.description,
          author: { '@type': 'Person', name: author.name, url: author.url },
          publisher,
          datePublished: article?.date || blogItemOverview?.date,
          dateModified:
            article?.updatedAt || article?.publishedAt || article?.date || blogItemOverview?.date,
          inLanguage: i18n.language,
          url: pageUrl,
          isPartOf: {
            '@type': 'Blog',
            name: 'Nordit Blog',
            url: blogListUrl,
          },
          keywords: [...articleTags, ...staticBlogTags].join(', ') || undefined,
        }
      : null;
  const schemaMarkup = [blogPostingSchema, breadcrumbSchema, ...faqSchema].filter(Boolean);
  const seoWithSchema = { ...seo, schemaMarkup };
  if (pageProps.data.type === 'content' && article) {
    return (
      <Layout
        className="page-light bg-gradient blog-content"
        footerBorderTop={false}
        seo={seoWithSchema}>
        <HeroContentArticle article={article} seoTitle={seo.title || ''} />
        <ContentArticleContent article={article} />
        <CTA
          dark
          fullHeight={false}
          subtitle={t('common:blogCtaSection.gotAProject')}
          text={t('common:blogCtaSection.text')}
          title={t('common:blogCtaSection.letsDiscussYourIdea')}
        />
        <BlogItemsLatest blogs={latestBlogs} dark />
      </Layout>
    );
  }
  if (!blog?.slug) {
    return (
      <Layout
        className="page-light bg-gradient blog-content"
        footerBorderTop={false}
        seo={seoWithSchema}>
        <CTA
          dark
          fullHeight={false}
          subtitle={t('common:blogCtaSection.gotAProject')}
          text={t('common:blogCtaSection.text')}
          title={t('common:blogCtaSection.letsDiscussYourIdea')}
        />
        <BlogItemsLatest blogs={latestBlogs} dark />
      </Layout>
    );
  }
  return (
    <Layout
      className="page-light bg-gradient blog-content"
      footerBorderTop={false}
      seo={seoWithSchema}>
      {!!blog && <HeroBlog blog={blog} seoTitle={seo.title || ''} />}
      {!!blog && <BlogContent blog={blog} />}
      <CTA
        dark
        fullHeight={false}
        subtitle={t('common:blogCtaSection.gotAProject')}
        text={t('common:blogCtaSection.text')}
        title={t('common:blogCtaSection.letsDiscussYourIdea')}
      />
      <BlogItemsLatest blogs={latestBlogs} dark />
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<IServerProps<IBlogSinglePageProps>> => {
  const key = getValidatedDynamicParam(context.query.key);

  if (!key) {
    return {
      notFound: true,
    };
  }

  if (context.query?.type) {
    return {
      redirect: {
        destination: `/blog/${key}`,
        permanent: true,
      },
    };
  }

  const tempLocale: IAnyType = await serverSideTranslations(context.locale || 'en', [
    'blogItemsOverview',
  ]);

  const blogItems: IBlogItem[] =
    tempLocale._nextI18Next.initialI18nStore[context.locale || 'en']?.blogItemsOverview
      ?.blogItems || [];
  const blogItem = blogItems.find(item => item.slug === `/blog/${key}`);
  const locale: IAnyType = await serverSideTranslations(context.locale || 'en', [
    blogItem?.detailsFileName || '',
    'blogItemsOverview',
    'common',
    'meta',
  ]);

  let article = null;
  try {
    const clientContent = createApolloClient();
    const publicationState = ENV_VARIABLES.APP_ENV !== 'production' ? 'DRAFT' : 'PUBLISHED';
    const { data: dataPosts } = await clientContent.query({
      query: QUERY_CONTENT_POSTS,
      variables: {
        filters: { slug: { eq: key } },
        locale: context.locale || 'en',
        status: publicationState,
        sort: ['date:desc'],
      },
    });
    const posts = dataPosts as unknown as { articles_connection: { nodes: Article[] } };
    const contentArticles = posts.articles_connection.nodes.map((a: Article) => a);
    article = contentArticles.find((a: Article) => a.slug === key) || null;
  } catch (error) {
    console.log('Error fetching posts:', error);
  }

  if (!blogItem && !article) {
    return {
      notFound: true,
    };
  }

  const type = article && !blogItem ? 'content' : 'blog';

  return {
    props: {
      data: {
        article,
        detailsFileName: blogItem?.detailsFileName || '',
        type,
        referer: context.req.headers.referer || null,
      },
      ...locale,
    },
  };
};

interface IBlogSinglePageProps {
  data:
    | {
        article: Article | null;
        detailsFileName: string;
        referer?: string | null;
        type: string;
      }
    | Record<string, never>;
}

export default BlogSinglePage;
